import { INTEGRATION_ENDPOINTS } from './endpoints.js';
/**
 * Integration API Client
 * Encapsulates all integration-related API calls for cross-domain operations.
 */
export class IntegrationClient {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * Get all integrated subscription plans.
     * Corresponds to: GET /api/integrated/plans
     */
    async getIntegratedPlans() {
        const response = await this.httpClient.get(INTEGRATION_ENDPOINTS.GET_PLANS);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get integrated plans');
        }
        return response.data;
    }
    /**
     * Get a single integrated subscription plan by its type.
     * Corresponds to: GET /api/integrated/plans/:planType
     */
    async getIntegratedPlan(planType) {
        const response = await this.httpClient.get(`${INTEGRATION_ENDPOINTS.GET_PLAN_BY_TYPE}${planType}`);
        if (!response.success) {
            throw new Error(response.error || `Failed to get integrated plan for ${planType}`);
        }
        // If success is true, data can be IntegratedPlan or null, which matches the return type.
        // If success is false, we've already thrown an error.
        // This handles the undefined case implicitly.
        return response.data ?? null;
    }
    /**
     * Upgrade a user's subscription plan.
     * Corresponds to: POST /api/integrated/subscription/upgrade
     */
    async upgradeSubscription(payload) {
        const response = await this.httpClient.post(INTEGRATION_ENDPOINTS.UPGRADE_SUBSCRIPTION, payload);
        if (!response.success) {
            throw new Error(response.message || 'Failed to upgrade subscription');
        }
        return response;
    }
    /**
     * Activate a coupon for the current user.
     * Corresponds to: POST /api/integrated/coupon-activation/activate
     */
    async activateCoupon(payload) {
        const response = await this.httpClient.post(INTEGRATION_ENDPOINTS.ACTIVATE_COUPON, payload);
        if (!response.success) {
            throw new Error(response.message || 'Failed to activate coupon');
        }
        return response;
    }
    /**
     * Initialize a new user's account (subscription, coupons, etc.).
     * Corresponds to: POST /api/integrated/user-initialization/initialize
     */
    async initializeUser() {
        const response = await this.httpClient.post(INTEGRATION_ENDPOINTS.INITIALIZE_USER, {});
        // The idempotency response returns a UserInitializationResponse without a nested `data` field.
        // We only need to check for the success flag.
        if (!response.success) {
            throw new Error(response.message || 'Failed to initialize user');
        }
        // The response object itself is the UserInitializationResponse.
        return response;
    }
    /**
     * Renew a user's subscription.
     * Corresponds to: POST /api/integrated/subscription/renewal
     */
    async renewSubscription(payload) {
        const response = await this.httpClient.post(INTEGRATION_ENDPOINTS.RENEW_SUBSCRIPTION, payload);
        if (!response.success) {
            throw new Error(response.message || 'Failed to renew subscription');
        }
        return response;
    }
    /**
     * Reset a user's subscription to the free plan.
     * Corresponds to: POST /api/integrated/subscription/reset-to-free
     */
    async resetSubscriptionToFree(payload) {
        const response = await this.httpClient.post(INTEGRATION_ENDPOINTS.RESET_SUBSCRIPTION_TO_FREE, payload);
        if (!response.success) {
            throw new Error(response.message || 'Failed to reset subscription to free');
        }
        return response;
    }
}
//# sourceMappingURL=client.js.map